import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export type ArchiveView = 'graph' | 'list';

/**
 * Which way the phone Knowledge Archive shows the concepts, kept in the URL:
 * `?view=list`, absent for the graph.
 *
 * In the URL for the same reason as Progress's period (`useProgressView`): a
 * phone leaves the page and comes back constantly - into a concept's lesson
 * and back - and a list reader should come back to the list. The change
 * REPLACES the entry, so Back leaves the archive rather than stepping between
 * the two views. Any other value reads as the graph.
 */
export function useArchiveView(): [ArchiveView, (view: ArchiveView) => void] {
  const [params, setParams] = useSearchParams();
  const view: ArchiveView = params.get('view') === 'list' ? 'list' : 'graph';

  const setView = useCallback(
    (next: ArchiveView) =>
      setParams(
        prev => {
          const out = new URLSearchParams(prev);
          if (next === 'list') out.set('view', 'list');
          else out.delete('view');
          return out;
        },
        { replace: true }
      ),
    [setParams]
  );

  return [view, setView];
}
